import { useEffect, useRef, useState } from "react";
import {
  startAsk,
  fetchSession,
  synthesize,
  type AskAnswer,
  type AskMode,
  type AskSession
} from "../lib/api";
import { Markdown } from "../components/Markdown";

const PROVIDER_LABEL: Record<string, { name: string; vendor: string }> = {
  anthropic: { name: "Claude", vendor: "Anthropic" },
  openai: { name: "GPT", vendor: "OpenAI" },
  gemini: { name: "Gemini", vendor: "Google" },
  synthesizer: { name: "Tổng hợp", vendor: "Claude Sonnet" }
};

export function Ask() {
  const [question, setQuestion] = useState("");
  const [mode, setMode] = useState<AskMode>("single");
  const [withContext, setWithContext] = useState(true);
  const [session, setSession] = useState<AskSession | null>(null);
  const [answers, setAnswers] = useState<AskAnswer[]>([]);
  const [loading, setLoading] = useState(false);
  const [synthLoading, setSynthLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<number | null>(null);

  useEffect(() => () => {
    if (pollRef.current) window.clearInterval(pollRef.current);
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!question.trim()) return;
    setLoading(true);
    setError(null);
    setAnswers([]);
    setSession(null);
    try {
      const { sessionId } = await startAsk({ question: question.trim(), mode, withContext });
      pollSession(sessionId);
    } catch (e) {
      setError((e as Error).message);
      setLoading(false);
    }
  }

  function pollSession(id: string) {
    if (pollRef.current) window.clearInterval(pollRef.current);
    let stopped = false;
    const tick = async () => {
      try {
        const data = await fetchSession(id);
        if (stopped) return;
        setSession(data.session);
        setAnswers(data.answers);
        const expected = data.session.mode === "compare" ? 3 : 1;
        const settled = data.answers.filter(
          (a) => a.provider !== "synthesizer" && (a.content !== null || a.error !== null)
        ).length;
        if (
          data.session.status === "complete" ||
          data.session.status === "failed" ||
          settled >= expected
        ) {
          stopped = true;
          if (pollRef.current) window.clearInterval(pollRef.current);
          setLoading(false);
        }
      } catch (e) {
        stopped = true;
        if (pollRef.current) window.clearInterval(pollRef.current);
        setError((e as Error).message);
        setLoading(false);
      }
    };
    tick();
    pollRef.current = window.setInterval(tick, 2000);
  }

  async function onSynthesize() {
    if (!session) return;
    setSynthLoading(true);
    try {
      await synthesize(session.id);
      const data = await fetchSession(session.id);
      setAnswers(data.answers);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSynthLoading(false);
    }
  }

  const synthesizerAnswer = answers.find((a) => a.provider === "synthesizer");
  const doctorAnswers = answers.filter((a) => a.provider !== "synthesizer");

  return (
    <div>
      {/* Form tile */}
      <section className="bg-canvas px-6 py-section md:px-12">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-displayLg">Hỏi điều gì?</h2>
          <p className="mt-4 text-bodyApple text-ink-muted80">
            Anh có thể hỏi 1 BS (Claude Sonnet) hoặc so sánh ý kiến 3 BS.
          </p>

          <form onSubmit={onSubmit} className="mt-10">
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="VD: Mấy hôm nay ngủ chỉ 5 tiếng, HRV xuống. Em có nên nghỉ tập tuần này không?"
              rows={4}
              className="w-full resize-none rounded-lg border border-hairline bg-canvas px-5 py-4 text-bodyApple text-ink placeholder:text-ink-muted48 focus:border-action focus:outline-none focus:ring-2 focus:ring-action/20"
            />

            <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
              <fieldset>
                <legend className="sr-only">Cách hỏi</legend>
                <div className="inline-flex rounded-pill border border-hairline bg-canvas-parchment p-1 text-captionApple">
                  {(["single", "compare"] as AskMode[]).map((m) => (
                    <button
                      type="button"
                      key={m}
                      onClick={() => setMode(m)}
                      className={`apple-press rounded-pill px-4 py-2 transition-colors ${
                        mode === m ? "bg-canvas text-ink shadow-sm" : "text-ink-muted80"
                      }`}
                    >
                      {m === "single" ? "Hỏi 1 BS" : "So sánh 3 BS"}
                    </button>
                  ))}
                </div>
              </fieldset>

              <label className="apple-press flex cursor-pointer items-center gap-3 text-captionApple text-ink-muted80">
                <input
                  type="checkbox"
                  checked={withContext}
                  onChange={(e) => setWithContext(e.target.checked)}
                  className="h-4 w-4 accent-action"
                />
                Đính kèm data Apple Watch + báo cáo gần nhất
              </label>

              <button
                type="submit"
                disabled={loading || !question.trim()}
                className="apple-press rounded-pill bg-action px-7 py-3 text-bodyApple text-white disabled:opacity-50"
              >
                {loading ? "Đang hỏi..." : "Gửi"}
              </button>
            </div>
          </form>

          {error && <p className="mt-6 text-captionApple text-[#a84a3a]">{error}</p>}
        </div>
      </section>

      {/* Answers — alternating tiles per Apple rhythm */}
      {doctorAnswers.length > 0 && (
        <section className="bg-tile-1 px-6 py-section text-white md:px-12">
          <div className="mx-auto max-w-6xl">
            <h3 className="text-displayMd">
              {session?.mode === "compare" ? "Ý kiến 3 BS" : "Ý kiến BS"}
            </h3>
            <div
              className={`mt-12 grid gap-8 ${
                doctorAnswers.length === 1
                  ? "grid-cols-1"
                  : "grid-cols-1 md:grid-cols-3"
              }`}
            >
              {doctorAnswers.map((a) => {
                const label = PROVIDER_LABEL[a.provider] ?? {
                  name: a.provider,
                  vendor: ""
                };
                return (
                  <article
                    key={a.provider}
                    className="rounded-lg border border-white/10 bg-tile-2 p-6"
                  >
                    <header className="flex items-baseline justify-between">
                      <h4 className="text-bodyStrong">{label.name}</h4>
                      <span className="text-finePrint text-bodyMuted">{label.vendor}</span>
                    </header>
                    <div className="mt-6 text-bodyApple text-white/90">
                      {a.error ? (
                        <p className="text-[#ff8a7a]">⚠️ {a.error}</p>
                      ) : a.content ? (
                        <Markdown md={a.content} onDark />
                      ) : (
                        <p className="animate-pulse text-bodyMuted">Đang chờ...</p>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>

            {session?.mode === "compare" &&
              !synthesizerAnswer &&
              doctorAnswers.filter((a) => a.content).length >= 2 && (
                <div className="mt-12 text-center">
                  <button
                    onClick={onSynthesize}
                    disabled={synthLoading}
                    className="apple-press rounded-pill bg-white px-7 py-3 text-bodyApple text-ink disabled:opacity-50"
                  >
                    {synthLoading ? "Đang tổng hợp..." : "✦ Tổng hợp ý kiến 3 BS"}
                  </button>
                </div>
              )}
          </div>
        </section>
      )}

      {synthesizerAnswer?.content && (
        <section className="bg-canvas-parchment px-6 py-section md:px-12">
          <div className="mx-auto max-w-3xl">
            <h3 className="text-displayMd">✦ Tổng hợp</h3>
            <p className="mt-4 text-captionApple text-ink-muted48">
              {PROVIDER_LABEL.synthesizer?.vendor} cross-check ý kiến 3 BS
            </p>
            <div className="mt-10">
              <Markdown md={synthesizerAnswer.content} />
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
